import React, { useState, useEffect, useRef } from 'react';
import { deductionsApi, unitsApi, aiApi, driversApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SmartNotes from '../components/SmartNotes';
import { DragDropZone } from '../components/DragDropZone';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  DollarSign, 
  Plus, 
  Trash2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Fuel,
  FileText,
  Shield,
  Wrench,
  Banknote,
  Upload,
  Paperclip,
  Download,
  X,
  File,
  ListPlus,
  Search,
  BarChart3,
  TrendingDown,
  Filter,
  Truck,
  Sparkles,
  Loader2,
  CheckCircle,
  AlertCircle,
  User,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getWeekStart, getWeekRange, addDaysYMD } from '../lib/utils';
import UnitSelectItems from '../components/UnitSelectItems';
import DateRangePicker from '../components/DateRangePicker';
import RecurringDeductions from './RecurringDeductions';
import { StatTile } from '../components/dashboard/KpiStrip';

const DEDUCTION_PRESETS = [
  { name: 'Fuel', icon: Fuel, type: 'standard' },
  { name: 'IFTA', icon: FileText, type: 'standard' },
  { name: 'ELD', icon: FileText, type: 'standard' },
  { name: 'Insurance', icon: Shield, type: 'standard' },
  { name: 'Cash Advance', icon: Banknote, type: 'standard' },
  { name: 'Repairs', icon: Wrench, type: 'standard' },
  { name: 'Maintenance', icon: Wrench, type: 'standard' },
  { name: 'Driver Pay', icon: DollarSign, type: 'standard' },
];

export default function Deductions() {
  const { canEdit } = useAuth();
  const [deductions, setDeductions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [weekStart, setWeekStart] = useState(getWeekStart());
  // Date filter mode: 'week' (default) or 'range' (custom range)
  const [dateMode, setDateMode] = useState('week');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    deduction_type: 'standard',
    notes: '',
    unit_id: '',
  });

  // Bulk deduction state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDeductions, setBulkDeductions] = useState([
    { name: '', amount: '', deduction_type: 'standard', notes: '', unit_id: '' }
  ]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk-delete selection state (mirrors the Loads page UX)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Inline-edit state — row id currently being edited and its draft values
  const [editingId, setEditingId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ name: '', amount: '', notes: '', unit_id: '', deduction_type: 'standard', week_start: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Main tab state
  const [activeTab, setActiveTab] = useState('weekly');

  // Reports state
  const [reportResults, setReportResults] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDatePreset, setReportDatePreset] = useState('this_year');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportUnit, setReportUnit] = useState('all');
  const [reportType, setReportType] = useState('all');
  const [reportViewMode, setReportViewMode] = useState('summary');

  // Fuel Report state
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [fuelProcessing, setFuelProcessing] = useState(false);
  const [fuelResults, setFuelResults] = useState(null);
  const [fuelApplying, setFuelApplying] = useState(false);
  const [fuelUnitOverrides, setFuelUnitOverrides] = useState({});

  const DATE_PRESETS = [
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year (YTD)' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const DEDUCTION_TYPES = [
    { value: 'all', label: 'All Types' },
    { value: 'Dispatch Fee', label: 'Dispatch Fee' },
    { value: 'Fuel', label: 'Fuel' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'Repairs', label: 'Repairs' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'ELD', label: 'ELD' },
    { value: 'IFTA', label: 'IFTA' },
    { value: 'Cash Advance', label: 'Cash Advance' },
    { value: 'Driver Pay', label: 'Driver Pay' },
  ];

  useEffect(() => {
    loadUnits();
    applyDatePreset('this_year');
  }, []);

  useEffect(() => {
    loadDeductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, selectedUnit, dateMode, rangeStart, rangeEnd]);

  const loadUnits = async () => {
    try {
      const res = await unitsApi.getAll();
      setUnits(res.data);
    } catch (error) {
      toast.error('Failed to load units');
    }
  };

  const loadDeductions = async () => {
    // In range mode, wait for both dates before fetching
    if (dateMode === 'range' && (!rangeStart || !rangeEnd)) return;

    setLoading(true);
    try {
      const params = {};
      if (dateMode === 'range') {
        params.start_date = rangeStart;
        params.end_date = rangeEnd;
      } else {
        params.week_start = weekStart;
      }
      if (selectedUnit !== 'all') {
        params.unit_id = selectedUnit;
      }
      const res = await deductionsApi.getAll(params);
      setDeductions(res.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Report functions
  const applyDatePreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch (preset) {
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3, 1);
        end = today;
        break;
      case 'last_quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        start = new Date(lastQuarterYear, adjustedQuarter * 3, 1);
        end = new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0);
        break;
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'custom':
        return;
      default:
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
    }
    
    setReportStartDate(start.toISOString().split('T')[0]);
    setReportEndDate(end.toISOString().split('T')[0]);
  };

  const handlePresetChange = (preset) => {
    setReportDatePreset(preset);
    applyDatePreset(preset);
  };

  const handleSearchReport = async () => {
    if (!reportStartDate || !reportEndDate) {
      toast.error('Please select a date range');
      return;
    }
    
    setReportLoading(true);
    try {
      const params = {
        start_date: reportStartDate,
        end_date: reportEndDate,
      };
      
      if (reportUnit !== 'all') {
        params.unit_id = reportUnit;
      }
      
      if (reportType !== 'all') {
        params.deduction_type = reportType;
      }
      
      const res = await deductionsApi.search(params);
      setReportResults(res.data);
    } catch (error) {
      toast.error('Failed to search deductions');
    } finally {
      setReportLoading(false);
    }
  };

  const formatReportDateRange = () => {
    if (!reportStartDate || !reportEndDate) return '';
    const start = new Date(reportStartDate);
    const end = new Date(reportEndDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getTypeIcon = (typeName) => {
    const iconMap = {
      'fuel': Fuel,
      'insurance': Shield,
      'repairs': Wrench,
      'maintenance': Wrench,
      'eld': FileText,
      'ifta': FileText,
      'cash advance': Banknote,
      'driver pay': DollarSign,
      'dispatch fee': Truck,
    };
    const Icon = iconMap[typeName.toLowerCase()] || DollarSign;
    return <Icon className="h-4 w-4" />;
  };

  const getCriticalTypes = () => ['Insurance', 'Fuel', 'Repairs', 'Maintenance', 'Dispatch Fee'];

  // Fuel Report handlers
  const handleFuelReportUpload = async (file) => {
    setFuelProcessing(true);
    setFuelResults(null);
    setFuelUnitOverrides({});
    try {
      const res = await aiApi.processFuelReport(file, weekStart);
      setFuelResults(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process fuel report');
    } finally {
      setFuelProcessing(false);
    }
  };

  const handleApplyFuelDeductions = async () => {
    if (!fuelResults) return;
    setFuelApplying(true);
    try {
      const entries = fuelResults.drivers
        .filter(d => !d.is_duplicate)
        .map(d => ({
          unit_id: fuelUnitOverrides[d.driver_name] || d.unit_id,
          total: d.total,
          notes: d.notes,
        }))
        .filter(e => e.unit_id); // Only include entries with a valid unit
      
      if (entries.length === 0) {
        toast.error('No valid entries to apply. Please assign units to all drivers.');
        setFuelApplying(false);
        return;
      }

      const targetWeek = fuelResults.week_start;
      const res = await aiApi.applyFuelDeductions(entries, targetWeek);
      const result = res.data;
      
      // Close dialog first
      setFuelDialogOpen(false);
      setFuelResults(null);
      setFuelUnitOverrides({});
      
      // Navigate to the correct week and refresh
      setWeekStart(targetWeek);
      
      // Show success toast
      toast.success(`Fuel deductions applied! Created ${result.created}, skipped ${result.skipped} duplicates.`);
      
      // Refresh deductions after a brief delay to ensure week navigation takes effect
      setTimeout(() => loadDeductions(), 300);
    } catch (error) {
      console.error('Fuel deduction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply fuel deductions. Please check if deductions were created and refresh.');
    } finally {
      setFuelApplying(false);
    }
  };

  // Filter deductions by search query — matches name, notes, OR amount.
  // For amount, we substring-match against the formatted number (e.g. "115" matches $115.00,
  // $1150.00, $211.50). This way the user can type any portion of the number.
  const filteredDeductions = searchQuery
    ? deductions.filter(d => {
        const q = searchQuery.toLowerCase().trim();
        if (d.name?.toLowerCase().includes(q)) return true;
        if (d.notes?.toLowerCase().includes(q)) return true;
        // Amount match — strip non-digit/decimal chars so "$1,150" or "1150" both work.
        const cleanQ = q.replace(/[^0-9.]/g, '');
        if (cleanQ) {
          const amtStr = String(d.amount ?? '');
          const amtFixed = Number(d.amount ?? 0).toFixed(2);
          if (amtStr.includes(cleanQ) || amtFixed.includes(cleanQ)) return true;
        }
        return false;
      })
    : deductions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let fileId = null;
      let fileName = null;

      // Upload file first if selected
      if (selectedFile) {
        setUploading(true);
        try {
          const uploadRes = await deductionsApi.uploadFile(selectedFile);
          fileId = uploadRes.data.file_id;
          fileName = uploadRes.data.filename;
        } catch (uploadError) {
          toast.error('Failed to upload file');
          setUploading(false);
          return;
        }
        setUploading(false);
      }

      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        week_start: weekStart,
        file_id: fileId,
        file_name: fileName,
      };

      await deductionsApi.create(data);
      toast.success('Deduction added successfully');
      setDialogOpen(false);
      resetForm();
      loadDeductions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) return;
    try {
      await deductionsApi.delete(id);
      toast.success('Deduction deleted successfully');
      loadDeductions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // ---- Edit-deduction modal helpers ----
  const startEdit = (d) => {
    setEditingId(d.id);
    setEditDraft({
      name: d.name || '',
      amount: String(d.amount ?? ''),
      notes: d.notes || '',
      unit_id: d.unit_id || '',
      deduction_type: d.deduction_type || 'standard',
      week_start: d.week_start || weekStart,
    });
    setEditDialogOpen(true);
  };
  const cancelEdit = () => {
    setEditDialogOpen(false);
    setEditingId(null);
    setEditDraft({ name: '', amount: '', notes: '', unit_id: '', deduction_type: 'standard', week_start: '' });
  };
  const saveEdit = async () => {
    if (!editDraft.name || !editDraft.amount || !editDraft.unit_id) {
      toast.error('Name, amount, and unit are required');
      return;
    }
    setSavingEdit(true);
    try {
      await deductionsApi.update(editingId, {
        name: editDraft.name,
        amount: parseFloat(editDraft.amount),
        deduction_type: editDraft.deduction_type || 'standard',
        notes: editDraft.notes,
        unit_id: editDraft.unit_id,
        week_start: editDraft.week_start,
      });
      toast.success('Deduction updated');
      cancelEdit();
      loadDeductions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- Bulk-delete helpers (mirror Loads page UX) ----
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const visibleIds = filteredDeductions.map(d => d.id);
      const allSelected = visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} deduction${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      // Run deletions in parallel; tolerate partial failures and report counts
      const results = await Promise.allSettled(ids.map(id => deductionsApi.delete(id)));
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      if (fail === 0) toast.success(`Deleted ${ok} deduction${ok !== 1 ? 's' : ''}`);
      else toast.warning(`Deleted ${ok} of ${results.length}; ${fail} failed`);
      setSelectedIds(new Set());
      loadDeductions();
    } catch (e) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      deduction_type: 'standard',
      notes: '',
      unit_id: '',
    });
    setSelectedFile(null);
    setUploadedFileInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File must be smaller than 100MB');
        return;
      }
      setSelectedFile(file);
      setUploadedFileInfo({
        name: file.name,
        size: file.size,
        type: file.type
      });
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setUploadedFileInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const res = await deductionsApi.downloadFile(fileId);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const selectPreset = (preset) => {
    setFormData({
      ...formData,
      name: preset.name,
      deduction_type: preset.type,
    });
  };

  // Bulk deduction handlers
  const addBulkRow = () => {
    setBulkDeductions([...bulkDeductions, { name: '', amount: '', deduction_type: 'standard', notes: '', unit_id: '' }]);
  };

  const removeBulkRow = (index) => {
    if (bulkDeductions.length > 1) {
      setBulkDeductions(bulkDeductions.filter((_, i) => i !== index));
    }
  };

  const updateBulkRow = (index, field, value) => {
    const updated = [...bulkDeductions];
    updated[index] = { ...updated[index], [field]: value };
    setBulkDeductions(updated);
  };

  const applyPresetToBulkRow = (index, preset) => {
    const updated = [...bulkDeductions];
    updated[index] = { ...updated[index], name: preset.name, deduction_type: preset.type };
    setBulkDeductions(updated);
  };

  const resetBulkForm = () => {
    setBulkDeductions([{ name: '', amount: '', deduction_type: 'standard', notes: '', unit_id: '' }]);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all rows
    const validDeductions = bulkDeductions.filter(d => d.name && d.amount && d.unit_id);
    if (validDeductions.length === 0) {
      toast.error('Please fill in at least one complete deduction (name, amount, and unit)');
      return;
    }

    setBulkSubmitting(true);
    try {
      const deductionsToCreate = validDeductions.map(d => ({
        name: d.name,
        amount: parseFloat(d.amount),
        deduction_type: d.deduction_type || 'standard',
        notes: d.notes || '',
        unit_id: d.unit_id,
        week_start: weekStart,
      }));

      await deductionsApi.createBulk(deductionsToCreate);
      toast.success(`Successfully added ${validDeductions.length} deduction(s)`);
      setBulkDialogOpen(false);
      resetBulkForm();
      loadDeductions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deductions');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const navigateWeek = (direction) => {
    setWeekStart(addDaysYMD(weekStart, direction * 7));
  };

  const getUnitNumber = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.unit_number || 'Unknown';
  };

  // Calculate totals from filtered deductions
  const totalDeductions = filteredDeductions.reduce((sum, d) => sum + d.amount, 0);

  // Group filtered deductions by unit
  const deductionsByUnit = filteredDeductions.reduce((acc, d) => {
    const unitNum = getUnitNumber(d.unit_id);
    if (!acc[unitNum]) acc[unitNum] = [];
    acc[unitNum].push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deductions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="dash-rise">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Settlements</p>
          <h1 className="text-3xl sm:text-4xl font-manrope font-bold tracking-tight text-slate-900 dark:text-white mt-1">Deductions</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-1">Manage weekly deductions and view reports</p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-[480px]">
          <TabsTrigger value="weekly" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Weekly
          </TabsTrigger>
          <TabsTrigger value="recurring" className="flex items-center gap-2" data-testid="recurring-tab">
            <RefreshCw className="h-4 w-4" />
            Recurring
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Weekly Deductions Tab */}
        <TabsContent value="weekly" className="space-y-4 mt-4">
          {/* Action Buttons */}
          {canEdit && (
            <div className="flex flex-wrap justify-end gap-2">
              {/* Fuel Report Upload Button */}
              <Dialog open={fuelDialogOpen} onOpenChange={(open) => {
                setFuelDialogOpen(open);
                if (!open) { setFuelResults(null); setFuelUnitOverrides({}); }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-green-200 text-green-700 hover:bg-green-50" data-testid="fuel-report-btn">
                    <Sparkles className="h-4 w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Upload </span>Fuel Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-manrope flex items-center gap-2">
                      <Fuel className="h-5 w-5 text-green-600" />
                      AI Fuel Report Processing
                    </DialogTitle>
                    <DialogDescription>
                      Upload a fuel report PDF. AI will extract each driver's fuel transactions and create deductions for their assigned units.
                    </DialogDescription>
                  </DialogHeader>

                  {!fuelResults ? (
                    <div className="space-y-4 py-4">
                      {fuelProcessing ? (
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                            <p className="text-sm font-medium">Processing fuel report...</p>
                            <p className="text-xs text-muted-foreground">AI is extracting driver transactions</p>
                          </div>
                        </div>
                      ) : (
                        <DragDropZone
                          onFileSelect={handleFuelReportUpload}
                          accept=".pdf"
                          maxSize={20 * 1024 * 1024}
                          data-testid="fuel-report-upload-zone"
                        >
                          <Fuel className="h-12 w-12 mx-auto text-green-400 mb-3" />
                          <p className="text-sm font-medium">Drag & drop or click to upload fuel report</p>
                          <p className="text-xs text-muted-foreground mt-1">PDF files only, up to 20MB</p>
                        </DragDropZone>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Fuel deductions will be applied to week: <strong>{getWeekRange(weekStart)}</strong>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 py-2">
                      {/* Results Summary */}
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                        <div>
                          <p className="text-sm font-medium">{fuelResults.report_name}</p>
                          <p className="text-xs text-muted-foreground">{fuelResults.drivers.length} drivers found</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium font-mono text-red-600">
                            -${fuelResults.drivers.reduce((s, d) => s + (d.is_duplicate ? 0 : d.total), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-muted-foreground">Total to deduct</p>
                        </div>
                      </div>

                      {/* Driver List */}
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                        {fuelResults.drivers.map((driver, idx) => {
                          const effectiveUnitId = fuelUnitOverrides[driver.driver_name] || driver.unit_id;
                          const effectiveUnit = effectiveUnitId
                            ? fuelResults.all_units.find(u => u.id === effectiveUnitId)
                            : null;

                          return (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg border ${driver.is_duplicate ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20' : driver.needs_unit_assignment && !fuelUnitOverrides[driver.driver_name] ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/20' : 'bg-card'}`}
                              data-testid={`fuel-driver-${idx}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-slate-500" />
                                  <span className="font-medium text-sm">{driver.driver_name}</span>
                                  {driver.matched_driver_name && driver.matched_driver_name !== driver.driver_name && (
                                    <span className="text-xs text-muted-foreground">({driver.matched_driver_name})</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {driver.is_duplicate && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Duplicate - Skip</span>
                                  )}
                                  <span className="font-mono text-sm font-medium text-red-600">
                                    -${driver.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>

                              {/* Unit assignment */}
                              <div className="flex items-center gap-2 mb-2">
                                <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                                {effectiveUnit ? (
                                  <span className="text-xs">Unit {effectiveUnit.unit_number}</span>
                                ) : (
                                  <Select
                                    value={fuelUnitOverrides[driver.driver_name] || ''}
                                    onValueChange={(val) => setFuelUnitOverrides(prev => ({ ...prev, [driver.driver_name]: val }))}
                                  >
                                    <SelectTrigger className="h-7 text-xs w-[180px]">
                                      <SelectValue placeholder="Select unit..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {fuelResults.all_units.map(u => (
                                        <SelectItem key={u.id} value={u.id}>{u.unit_number}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                                {!effectiveUnit && !driver.is_duplicate && (
                                  <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                                )}
                              </div>

                              {/* Transaction notes preview */}
                              <p className="text-xs text-muted-foreground truncate">{driver.notes}</p>
                            </div>
                          );
                        })}
                      </div>

                      <DialogFooter className="pt-2 border-t">
                        <Button variant="outline" onClick={() => { setFuelResults(null); setFuelUnitOverrides({}); }}>
                          Upload Different Report
                        </Button>
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          onClick={handleApplyFuelDeductions}
                          disabled={fuelApplying}
                          data-testid="apply-fuel-btn"
                        >
                          {fuelApplying ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>
                          ) : (
                            <><CheckCircle className="h-4 w-4 mr-2" /> Apply Fuel Deductions</>
                          )}
                        </Button>
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Bulk Add Button */}
              <Dialog open={bulkDialogOpen} onOpenChange={(open) => {
                setBulkDialogOpen(open);
                if (!open) resetBulkForm();
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="bulk-add-deduction-btn">
                    <ListPlus className="h-4 w-4 mr-1 sm:mr-2" />
                    Bulk Add
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[800px]">
                  <DialogHeader>
                    <DialogTitle className="font-manrope">Add Multiple Deductions</DialogTitle>
                    <DialogDescription>
                      Add multiple deductions at once for week of {getWeekRange(weekStart)}
                    </DialogDescription>
                  </DialogHeader>
                    <form onSubmit={handleBulkSubmit} className="space-y-4 pb-4">
                      {/* Bulk Deduction Rows */}
                      <div className="space-y-4">
                    {bulkDeductions.map((row, index) => (
                      <div key={index} className="p-4 border rounded-lg bg-muted/30 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Deduction #{index + 1}</span>
                          {bulkDeductions.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeBulkRow(index)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Quick Presets */}
                        <div className="flex flex-wrap gap-1">
                          {DEDUCTION_PRESETS.map((preset) => (
                            <Button
                              key={preset.name}
                              type="button"
                              variant={row.name === preset.name ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => applyPresetToBulkRow(index, preset)}
                            >
                              {preset.name}
                            </Button>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Unit *</Label>
                            <Select
                              value={row.unit_id}
                              onValueChange={(value) => updateBulkRow(index, 'unit_id', value)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <UnitSelectItems units={units} hideInactive />
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Name *</Label>
                            <Input
                              value={row.name}
                              onChange={(e) => updateBulkRow(index, 'name', e.target.value)}
                              placeholder="e.g., Fuel"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Amount *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={row.amount}
                              onChange={(e) => updateBulkRow(index, 'amount', e.target.value)}
                              placeholder="0.00"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Notes</Label>
                            <Input
                              value={row.notes}
                              onChange={(e) => updateBulkRow(index, 'notes', e.target.value)}
                              placeholder="Optional"
                              className="h-9"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Row Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addBulkRow}
                    className="w-full border-dashed"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Deduction
                  </Button>

                  <DialogFooter className="pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={bulkSubmitting}
                    >
                      {bulkSubmitting ? 'Adding...' : `Add ${bulkDeductions.filter(d => d.name && d.amount && d.unit_id).length} Deduction(s)`}
                    </Button>
                  </DialogFooter>
                </form>
            </DialogContent>
          </Dialog>

          {/* Single Add Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-deduction-btn">
                <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                Add Deduction
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-manrope">Add Deduction</DialogTitle>
                <DialogDescription>
                  Add a deduction for week of {getWeekRange(weekStart)}
                </DialogDescription>
              </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pb-4">
                {/* Quick Presets */}
                <div className="space-y-2">
                  <Label>Quick Select</Label>
                  <div className="flex flex-wrap gap-2">
                    {DEDUCTION_PRESETS.map((preset) => (
                      <Button
                        key={preset.name}
                        type="button"
                        variant={formData.name === preset.name ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => selectPreset(preset)}
                        className={formData.name === preset.name ? 'bg-blue-600' : ''}
                      >
                        <preset.icon className="h-3 w-3 mr-1" />
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unit_id">Unit *</Label>
                  <Select
                    value={formData.unit_id}
                    onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="deduction-unit-select">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <UnitSelectItems units={units} hideInactive />
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Deduction Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Fuel, Insurance"
                      required
                      data-testid="deduction-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="deduction-amount-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deduction_type">Type</Label>
                  <Select
                    value={formData.deduction_type}
                    onValueChange={(value) => setFormData({ ...formData, deduction_type: value })}
                  >
                    <SelectTrigger data-testid="deduction-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <SmartNotes
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes... (Math: 100+200 or 1000*10%)"
                    rows={2}
                    data-testid="deduction-notes-input"
                  />
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Attachment (Optional)</Label>
                  <div>
                    {!uploadedFileInfo ? (
                      <DragDropZone
                        onFileSelect={handleFileSelect}
                        accept="*"
                        maxSize={100 * 1024 * 1024}
                        className="p-4"
                        data-testid="deduction-file-upload-zone"
                      >
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Drag & drop or click to upload receipt, invoice, or document
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Max file size: 100MB</p>
                      </DragDropZone>
                    ) : (
                      <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <File className="h-8 w-8 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px]">
                              {uploadedFileInfo.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(uploadedFileInfo.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={removeSelectedFile}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter className="flex-shrink-0 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700" 
                    data-testid="deduction-submit-btn"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                        Uploading...
                      </>
                    ) : (
                      'Add Deduction'
                    )}
                  </Button>
                </DialogFooter>
              </form>
          </DialogContent>
        </Dialog>
        </div>
      )}

      {/* Week Navigation & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <DateRangePicker
              mode={dateMode}
              onModeChange={setDateMode}
              weekStart={weekStart}
              onWeekStartChange={setWeekStart}
              startDate={rangeStart}
              endDate={rangeEnd}
              onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
            />

            {/* Unit Filter & Search */}
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, notes, or amount…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[200px]"
                  data-testid="deduction-search-input"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              
              <Select
                value={selectedUnit}
                onValueChange={setSelectedUnit}
              >
                <SelectTrigger className="w-[200px]" data-testid="unit-filter-select">
                  <SelectValue placeholder="Filter by unit" />
                </SelectTrigger>
                <SelectContent>
                  <UnitSelectItems units={units} includeAll allLabel="All Units" />
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Results Info */}
      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Search className="h-4 w-4" />
          Found {filteredDeductions.length} deduction{filteredDeductions.length !== 1 ? 's' : ''} matching "{searchQuery}"
          {filteredDeductions.length !== deductions.length && (
            <span>(out of {deductions.length} total)</span>
          )}
        </div>
      )}

      {/* Total */}
      {filteredDeductions.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deductions-week-stats">
          <StatTile label={searchQuery ? 'Filtered Deductions Total' : 'Total Deductions This Week'} value={`−${formatCurrency(totalDeductions)}`} tone="primary" className="col-span-2 lg:col-span-1" testid="deductions-stat-total" />
          <StatTile label="Deduction Items" value={filteredDeductions.length} accent="#1e3a8a" delay={60} testid="deductions-stat-count" />
          <StatTile label="Units Affected" value={Object.keys(deductionsByUnit).length} accent="#60a5fa" delay={120} testid="deductions-stat-units" />
        </div>
      )}

      {/* Bulk-delete action bar (only visible when rows are selected) */}
      {canEdit && selectedIds.size > 0 && (
        <Card className="bg-rose-50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-900" data-testid="bulk-delete-bar">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-rose-700 dark:text-rose-300">
              {selectedIds.size} selected
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
              <Button
                variant="destructive" size="sm"
                onClick={handleBulkDelete} disabled={bulkDeleting}
                data-testid="bulk-delete-confirm"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deductions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-[44px]">
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer"
                        checked={filteredDeductions.length > 0 && filteredDeductions.every(d => selectedIds.has(d.id))}
                        onChange={toggleSelectAll}
                        data-testid="bulk-select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="sticky left-0 z-20 bg-background sm:static sm:bg-transparent">Unit</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead>Deduction</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Attachment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {deductions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 6} className="text-center py-8 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <DollarSign className="h-8 w-8 text-muted-foreground/50" />
                        <p>{dateMode === 'range' ? 'No deductions in this date range' : 'No deductions for this week'}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeductions.map((deduction) => (
                    <TableRow key={deduction.id} data-testid={`deduction-row-${deduction.id}`}>
                      {canEdit && (
                        <TableCell className="w-[44px]">
                          {deduction.deduction_type === 'dispatch_fee' ? (
                            <span className="text-muted-foreground text-xs" title="Auto-generated from load — not editable">—</span>
                          ) : (
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer"
                              checked={selectedIds.has(deduction.id)}
                              onChange={() => toggleSelect(deduction.id)}
                              data-testid={`bulk-select-${deduction.id}`}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell className="font-medium sticky left-0 z-10 bg-background sm:static sm:bg-transparent">
                        {getUnitNumber(deduction.unit_id)}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {deduction.week_start || '-'}
                      </TableCell>
                      <TableCell>{deduction.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px]">
                        <span className="truncate block">{deduction.notes || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {deduction.file_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 p-0 h-auto"
                            onClick={() => handleDownloadFile(deduction.file_id, deduction.file_name)}
                            data-testid={`download-file-${deduction.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            <span className="text-xs truncate max-w-[100px]">
                              {deduction.file_name || 'Download'}
                            </span>
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        -{formatCurrency(deduction.amount)}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          {deduction.deduction_type === 'dispatch_fee' ? (
                            <span className="text-xs text-muted-foreground italic" title="Dispatch fees are managed on the load itself">from load</span>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => startEdit(deduction)}
                                data-testid={`edit-deduction-${deduction.id}`}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(deduction.id)}
                                className="text-red-600 hover:text-red-700 h-7 w-7"
                                data-testid={`delete-deduction-${deduction.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Deduction Modal */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-deduction-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Edit Deduction</DialogTitle>
            <DialogDescription>Update fields and click Save to apply changes.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); saveEdit(); }}
            className="space-y-4 pb-2"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-unit">Unit *</Label>
              <Select
                value={editDraft.unit_id}
                onValueChange={(v) => setEditDraft({ ...editDraft, unit_id: v })}
              >
                <SelectTrigger data-testid="edit-deduction-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  <UnitSelectItems units={units} hideInactive />
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  placeholder="e.g., Fuel"
                  data-testid="edit-deduction-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-amount">Amount *</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  value={editDraft.amount}
                  onChange={(e) => setEditDraft({ ...editDraft, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="edit-deduction-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Type</Label>
              <Select
                value={editDraft.deduction_type}
                onValueChange={(v) => setEditDraft({ ...editDraft, deduction_type: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={editDraft.notes}
                onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                placeholder="Optional"
                data-testid="edit-deduction-notes"
              />
            </div>

            <DialogFooter className="pt-2 border-t">
              <Button type="button" variant="outline" onClick={cancelEdit} data-testid="edit-deduction-cancel">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={savingEdit}
                data-testid="edit-deduction-save"
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </TabsContent>

      {/* Recurring Tab — embedded RecurringDeductions page (auto-applies each week) */}
      <TabsContent value="recurring" className="space-y-4 mt-4">
        <RecurringDeductions embedded />
      </TabsContent>

      {/* Reports Tab */}
      <TabsContent value="reports" className="space-y-4 mt-4">
        {/* Search Filters */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Search Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Range Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Date Range Preset</Label>
                <Select value={reportDatePreset} onValueChange={handlePresetChange}>
                  <SelectTrigger data-testid="date-preset-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map(preset => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => {
                    setReportStartDate(e.target.value);
                    setReportDatePreset('custom');
                  }}
                  data-testid="report-start-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => {
                    setReportEndDate(e.target.value);
                    setReportDatePreset('custom');
                  }}
                  data-testid="report-end-date"
                />
              </div>

              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={reportUnit} onValueChange={setReportUnit}>
                  <SelectTrigger data-testid="report-unit-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <UnitSelectItems units={units} includeAll allLabel="All Units (Company Total)" />
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type Filter Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Deduction Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger data-testid="report-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEDUCTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-3 flex items-end">
                <Button 
                  onClick={handleSearchReport} 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={reportLoading}
                  data-testid="search-report-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {reportLoading ? 'Searching...' : 'Search Deductions'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Results */}
        {reportLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : reportResults ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700 font-medium">Total Deductions</p>
                      <p className="text-2xl font-bold text-red-800 font-mono">
                        -{formatCurrency(reportResults.total_amount)}
                      </p>
                    </div>
                    <TrendingDown className="h-10 w-10 text-red-400" />
                  </div>
                  <p className="text-xs text-red-600 mt-2">{formatReportDateRange()}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Total Entries</p>
                      <p className="text-2xl font-bold">{reportResults.total_count}</p>
                    </div>
                    <BarChart3 className="h-10 w-10 text-blue-400" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Deduction records</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Deduction Types</p>
                      <p className="text-2xl font-bold">{Object.keys(reportResults.summary_by_type).length}</p>
                    </div>
                    <FileText className="h-10 w-10 text-slate-400" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Different categories</p>
                </CardContent>
              </Card>
            </div>

            {/* View Toggle */}
            <Tabs value={reportViewMode} onValueChange={setReportViewMode}>
              <TabsList>
                <TabsTrigger value="summary" data-testid="summary-tab">Summary View</TabsTrigger>
                <TabsTrigger value="detailed" data-testid="detailed-tab">Detailed / Itemized</TabsTrigger>
              </TabsList>

              {/* Summary View */}
              <TabsContent value="summary" className="space-y-6">
                {/* Summary by Type */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Summary by Deduction Type</CardTitle>
                    <CardDescription>Total amounts grouped by deduction category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Deduction Type</TableHead>
                          <TableHead className="text-center">Occurrences</TableHead>
                          <TableHead className="text-center">Weeks Applied</TableHead>
                          <TableHead className="text-right">Total Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(reportResults.summary_by_type)
                          .sort((a, b) => b[1].total - a[1].total)
                          .map(([typeName, data]) => (
                            <TableRow key={typeName} className={getCriticalTypes().includes(typeName) ? 'bg-amber-50/50' : ''}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {getTypeIcon(typeName)}
                                  {typeName}
                                  {getCriticalTypes().includes(typeName) && (
                                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                      Key
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{data.count}</TableCell>
                              <TableCell className="text-center">{data.week_count}</TableCell>
                              <TableCell className="text-right font-mono text-red-600 font-medium">
                                -{formatCurrency(data.total)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Summary by Unit */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Summary by Unit</CardTitle>
                    <CardDescription>Total deductions per unit with breakdown by type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="multiple" className="w-full">
                      {Object.entries(reportResults.summary_by_unit)
                        .sort((a, b) => b[1].total - a[1].total)
                        .map(([unitNum, data]) => (
                          <AccordionItem key={unitNum} value={unitNum}>
                            <AccordionTrigger className="hover:no-underline">
                              <div className="flex items-center justify-between w-full pr-4">
                                <div className="flex items-center gap-3">
                                  <Truck className="h-5 w-5 text-slate-500" />
                                  <span className="font-medium">Unit {unitNum}</span>
                                  <Badge variant="secondary">{data.count} deductions</Badge>
                                </div>
                                <span className="font-mono text-red-600 font-medium">
                                  -{formatCurrency(data.total)}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="pl-8 pt-2">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Type</TableHead>
                                      <TableHead className="text-right">Amount</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {Object.entries(data.by_type)
                                      .sort((a, b) => b[1] - a[1])
                                      .map(([typeName, amount]) => (
                                        <TableRow key={typeName}>
                                          <TableCell className="flex items-center gap-2">
                                            {getTypeIcon(typeName)}
                                            {typeName}
                                          </TableCell>
                                          <TableCell className="text-right font-mono text-red-600">
                                            -{formatCurrency(amount)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Detailed View */}
              <TabsContent value="detailed" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Itemized Deductions</CardTitle>
                    <CardDescription>
                      All deductions with dates showing when each was charged
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Week</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportResults.deductions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                              No deductions found for the selected criteria
                            </TableCell>
                          </TableRow>
                        ) : (
                          reportResults.deductions.map((deduction, index) => (
                            <TableRow key={deduction.id || index} data-testid={`report-deduction-row-${index}`}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{getWeekRange(deduction.week_start)}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Truck className="h-4 w-4 text-slate-500" />
                                  <span className="font-medium">{deduction.unit_number}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {getTypeIcon(deduction.name)}
                                  {deduction.name}
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                                {deduction.notes || '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-red-600 font-medium">
                                -{formatCurrency(deduction.amount)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <Card className="py-12">
            <CardContent className="flex flex-col items-center justify-center text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">Search for Deductions</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Select a date range and filters above to search for deductions.
                View summaries by type or unit, or see itemized details with dates.
              </p>
            </CardContent>
          </Card>
        )}
      </TabsContent>
      </Tabs>
    </div>
  );
}
